/**
 * CodexPreviewCard — floating hover/tap preview for Story Bible entities.
 *
 * Listens at the document level for mouseover (desktop) and click (mobile)
 * events on `.codex-entity` elements produced by the CodexHighlight extension.
 * When triggered it looks up the full BibleEntry and renders a small card
 * positioned just below (or above, if near the bottom) the highlighted text.
 *
 * The component mounts no visible DOM when there are no entries.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import type { BibleEntry, BibleEntryType } from '../lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CodexPreviewCardProps {
  /** Full list of bible entries — used to look up name, content, imageUrl */
  entries: BibleEntry[];
  /**
   * Called when the user clicks a `.codex-entity` element, with the intent
   * of opening the full dossier for that entry. Optional so the card can
   * still be used purely as a hover preview elsewhere.
   */
  onOpenEntry?: (entry: BibleEntry) => void;
}

interface CardState {
  entry: BibleEntry;
  top: number;
  left: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CARD_WIDTH = 244;
const CARD_ESTIMATE_HEIGHT = 180; // conservative estimate for flip-up logic

const TYPE_LABELS: Record<BibleEntryType, string> = {
  character: 'Personagem',
  location: 'Local',
  lore: 'Lore',
  item: 'Item',
  organization: 'Organização',
  note: 'Nota',
};

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function getCardPosition(
  rect: DOMRect,
): { top: number; left: number } {
  const spaceBelow = window.innerHeight - rect.bottom;
  const top =
    spaceBelow >= CARD_ESTIMATE_HEIGHT + 12
      ? rect.bottom + 8
      : rect.top - CARD_ESTIMATE_HEIGHT - 8;

  const left = Math.min(
    Math.max(8, rect.left),
    window.innerWidth - CARD_WIDTH - 8,
  );

  return { top, left };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CodexPreviewCard({ entries, onOpenEntry }: CodexPreviewCardProps) {
  const [card, setCard] = useState<CardState | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // Track the DOM element that anchors the current card so we can reposition
  // it on scroll without losing the reference to which entity is open.
  const anchorElRef = useRef<Element | null>(null);

  // Build a lookup map from entry id → entry (refreshed when entries change)
  const byId = useRef<Map<string, BibleEntry>>(new Map());
  useEffect(() => {
    byId.current = new Map(entries.map((e) => [e.id, e]));
  }, [entries]);

  const showCard = useCallback((el: Element) => {
    const id = el.getAttribute('data-codex-id');
    if (!id) return;
    const entry = byId.current.get(id);
    if (!entry) return;
    const rect = el.getBoundingClientRect();
    const { top, left } = getCardPosition(rect);
    anchorElRef.current = el;
    setCard({ entry, top, left });
  }, []);

  const hideCard = useCallback(() => {
    anchorElRef.current = null;
    setCard(null);
  }, []);

  // Reposition the card to track its anchor element on scroll.
  // If the anchor element has scrolled out of the viewport, hide the card.
  const repositionCard = useCallback(() => {
    const el = anchorElRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const offScreen =
      rect.bottom < 0 ||
      rect.top > window.innerHeight ||
      rect.right < 0 ||
      rect.left > window.innerWidth;
    if (offScreen) {
      hideCard();
      return;
    }
    const { top, left } = getCardPosition(rect);
    setCard((prev) => (prev ? { ...prev, top, left } : null));
  }, [hideCard]);

  // Attach a passive scroll listener (capture phase catches nested scroll
  // containers such as the editor overflow-auto wrapper) whenever a card is
  // visible, and detach it once the card is hidden.
  useEffect(() => {
    if (!card) return;
    const onScroll = () => repositionCard();
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
    };
  }, [card, repositionCard]);

  useEffect(() => {
    if (entries.length === 0) return;

    const handleMouseOver = (e: MouseEvent) => {
      const el = (e.target as Element).closest('.codex-entity');
      if (el) showCard(el);
    };

    const handleMouseOut = (e: MouseEvent) => {
      const rel = e.relatedTarget as Element | null;
      // Keep card visible while moving into the card itself or another entity
      if (
        rel?.closest('.codex-entity') ||
        (cardRef.current && cardRef.current.contains(rel))
      ) {
        return;
      }
      hideCard();
    };

    // Click on an entity opens the full dossier (desktop & mobile). Falls
    // back to the hover-preview card when no `onOpenEntry` handler is given.
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;
      const el = target.closest('.codex-entity');
      if (el) {
        // Don't hijack the click if the user is finishing a text selection.
        if (window.getSelection()?.toString()) return;

        if (onOpenEntry) {
          const id = el.getAttribute('data-codex-id');
          const entry = id ? byId.current.get(id) : undefined;
          if (entry) {
            hideCard();
            onOpenEntry(entry);
            return;
          }
        }
        // No handler (or entry not found) — fall back to tap-to-preview.
        showCard(el);
        return;
      }
      // Click outside card & entity → hide
      if (cardRef.current && !cardRef.current.contains(target)) {
        hideCard();
      }
    };

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      document.removeEventListener('click', handleClick);
    };
  }, [entries, showCard, hideCard, onOpenEntry]);

  if (!card) return null;

  const { entry, top, left } = card;

  return (
    <div
      ref={cardRef}
      className="codex-preview-card"
      style={{ position: 'fixed', top, left, width: CARD_WIDTH, zIndex: 9998 }}
      onMouseLeave={hideCard}
    >
      {entry.imageUrl && (
        <img
          src={entry.imageUrl}
          alt={entry.name}
          className="codex-preview-card__image"
        />
      )}
      <div className="codex-preview-card__body">
        <div className="codex-preview-card__header">
          <span className="codex-preview-card__name">{entry.name}</span>
          <span className={`codex-preview-card__badge codex-badge-${entry.type}`}>
            {TYPE_LABELS[entry.type]}
          </span>
        </div>
        {entry.content && (
          <p className="codex-preview-card__description">
            {entry.content.length > 130
              ? `${entry.content.slice(0, 130)}…`
              : entry.content}
          </p>
        )}
      </div>
    </div>
  );
}
